/**
 * shell/Sidebar.tsx —— 座位 `sidebar`（壳规范 §3：**左栏＝会话列表，不是视图平铺**）。
 *
 * 三段（声明序＝`shell.decl.yaml` sidebar.segments）：**品牌行**（`sidebar.brand.mark`／`sidebar.brand.name`）
 * ＋ **会话列表**（有源即列/可切、计数随源；无源 ⇒ 空态＋原因 `sidebar.sessions_empty`；**禁假造条目**）
 * ＋ **设置入口**（§7；全 UI 唯一登出口在设置面板内）。
 * 纪律：R1 —— 导航位**只放对象域/任务**（会话），**禁把 `views/*.yaml` 平铺成左栏项**；
 *      本件不取数、不造清单；色/间距/字号全走 token。
 * 🔴 大王令：左栏**不得**有账号菜单/账号入口/登出项（四处禁列 testid 一律不复列）；
 *    新增会话＝声明动作（`sidebar.new_session`；无宿主绑定 ⇒ 显式禁用＋可见原因）。
 */
import React, { useEffect, useRef, useState } from "react";
import { tokenVar } from "../theme/tokens";
import { SHELL_DECL } from "./shellDecl";
import type { SessionItem } from "./types";


export interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  sessions: SessionItem[];
  activeSessionId?: string;
  onPickSession?: (id: string) => void;
  onCreateSession?: () => void;
  /** ★L-PL-5c：会话行删除动作（决定动作——**禁常驻成钮**：hover/焦点内揭示＋两击确认）；
   *  未绑定 ⇒ 不渲染删除入口（不假装可用）。语义＝服务端软删（数据永存）。 */
  onDeleteSession?: (id: string) => void;
  onOpenSettings: () => void;
  settingsOpen: boolean;
  /** 当前主体（服务端回执／宿主注入；缺省 ⇒ 未登录态＝「更多」三点行） */
  actor?: string;
  /** 座位 testid（覆盖态抽屉与常驻态共用同一座位语义） */
  testId?: string;
}

/** 座位 `sidebar`（常驻态与抽屉态共用；`collapsed` ⇒ 56px 控制轨） */
export function Sidebar(props: SidebarProps): React.ReactElement {
  const { collapsed } = props;
  const sessions = props.sessions ?? [];
  /** §3 新增会话＝**动作声明**：无宿主绑定 ⇒ 显式禁用＋可见原因（禁假造会话条目） */
  const newSession = SHELL_DECL.sidebar.new_session;
  const createBound = typeof props.onCreateSession === "function";
  /** ★L-PL-5c：删除入口绑定面（未绑定 ⇒ 行内不渲染删除入口） */
  const deleteBound = typeof props.onDeleteSession === "function";
  /**
   * ★L-PL-5c：删除＝**决定动作**，两击确认（承 `ModelCatalogSection`「删除模型」既有范式：
   * 首击 arm（文案换「确认删除」）、3 秒内二击才真删，超时自动解除）；再配 hover/焦点内揭示
   * （UI 铁律「决定动作禁常驻成钮」）。
   */
  const [armedId, setArmedId] = useState<string | null>(null);
  const armTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (armTimer.current) clearTimeout(armTimer.current); }, []);
  const onDelClick = (id: string): void => {
    if (armedId !== id) {
      setArmedId(id);
      if (armTimer.current) clearTimeout(armTimer.current);
      armTimer.current = setTimeout(() => setArmedId(null), 3000);
      return;
    }
    if (armTimer.current) clearTimeout(armTimer.current);
    setArmedId(null);
    props.onDeleteSession?.(id);
  };
  return (
    <nav
      data-testid={props.testId ?? "seat-sidebar"}
      data-seat="sidebar"
      data-collapsed={collapsed ? "true" : "false"}
      className="xg-sidebar"
      aria-label="会话"
    >
      <div className="xg-sidebar-brand" data-testid="sidebar-brand">
        <span className="xg-brand-mark" data-testid="sidebar-brand-mark" aria-hidden="true" />
        {!collapsed ? <strong data-testid="sidebar-brand-name">Sononux</strong> : null}
        <button
          type="button"
          className="xg-btn xg-btn-icon"
          data-testid="sidebar-toggle"
          aria-expanded={!collapsed}
          aria-label={collapsed ? "展开左栏" : "折叠左栏"}
          onClick={props.onToggle}
          style={{ marginLeft: "auto" }}
        >{collapsed ? "»" : "«"}</button>
      </div>

      <div className="xg-sidebar-body xg-scroll-quiet" data-testid="sidebar-sessions" data-visible={collapsed ? "false" : "true"}>
        {
          <>
            <button
              type="button"
              className="xg-btn xg-btn-new-session"
              data-testid="session-new"
              data-action={newSession.action}
              data-bound={createBound ? "true" : "false"}
              aria-label={newSession.aria_label}
              aria-disabled={createBound ? undefined : "true"}
              aria-describedby={createBound ? undefined : "session-new-reason"}
              disabled={!createBound}
              onClick={props.onCreateSession}
              style={{ marginBottom: tokenVar("space-xs") }}
            >＋ {newSession.label}</button>
            <ul
              data-testid="session-list"
              data-empty={sessions.length === 0 ? "true" : "false"}
              style={{ listStyle: "none", margin: 0, padding: 0 }}
            >
              {sessions.map((session) => {
                const armed = armedId === session.id;
                return (
                <li key={session.id} className="xg-session-row" data-testid={"session-row-" + session.id}>
                  <button
                    type="button"
                    className="xg-session"
                    data-testid={"session-" + session.id}
                    aria-current={session.id === props.activeSessionId ? "true" : undefined}
                    onClick={() => props.onPickSession?.(session.id)}
                  >
                    <span>{session.title}</span>
                    {/* ★20261005 报障「大量描述的文本」同族订正：第二行＝**时间**（102 `formatSessionTime` 语义），
                        不再直渲 `activity_state` 裸 token（生产每行一个「idle」×200 ＝ 噪声）；
                        缺时间 ⇒ 时间位不渲（如实留空，不补造）。 */}
                    {session.time ? (
                      <span
                        data-testid={"session-time-" + session.id}
                        style={{ display: "block", color: tokenVar("text-tertiary"), fontSize: tokenVar("font-size-caption") }}
                      >
                        {session.time}
                      </span>
                    ) : null}
                  </button>
                  {/* ★L-PL-5c 删除入口（决定动作）：hover/焦点内揭示（`styles/shell.css` `.xg-session-del`），
                      两击确认（arm 3s）；未绑定 ⇒ 不渲染（不假装可用）。 */}
                  {deleteBound ? (
                    <button
                      type="button"
                      className={"xg-session-del" + (armed ? " xg-session-del--armed" : "")}
                      data-testid={"session-del-" + session.id}
                      aria-label={(armed ? "确认删除会话 " : "删除会话 ") + session.title}
                      title={armed ? "再点一次确认删除（3 秒后自动取消）" : "删除会话（软删，数据永存）"}
                      onClick={() => onDelClick(session.id)}
                    >{armed ? "确认删除" : "删除"}</button>
                  ) : null}
                </li>
                );
              })}
            </ul>
            {/* §3 无源 ⇒ **空态＋原因**（声明 `sidebar.sessions_empty`）；**禁假造条目** */}
            {sessions.length === 0 ? (
              <p
                className="xg-hint"
                data-testid="session-list-empty"
                data-visual="xg-sidebar-empty"
                style={{ margin: 0, padding: "0 " + tokenVar("space-xs") }}
              >{SHELL_DECL.sidebar.sessions_empty}</p>
            ) : null}
            {/* 新增会话不可用原因：**可见**（声明 `sidebar.new_session.absent_note`）——禁「点了没反应」 */}
            {createBound ? null : (
              <p
                id="session-new-reason"
                data-testid="session-new-reason"
                className="xg-hint"
                style={{ margin: 0, padding: "0 " + tokenVar("space-xs") }}
              >{newSession.absent_note}</p>
            )}
          </>
        }
      </div>

      <div className="xg-sidebar-footer" data-testid="sidebar-footer">
        <button
          type="button"
          className="xg-btn"
          data-testid="settings-entry"
          aria-label="设置"
          aria-haspopup="dialog"
          aria-expanded={props.settingsOpen}
          onClick={props.onOpenSettings}
          style={{ width: "100%", justifyContent: collapsed ? "center" : "flex-start" }}
        >⚙{collapsed ? "" : " 设置"}</button>

      </div>
    </nav>
  );
}
